import {z} from 'zod';

export const carPassApiStateSchema = z.enum([
  'PENDING',
  'WAITING_FOR_DIV',
  'ACTION_REQUIRED',
  'ACTION_TAKEN',
  'READY',
  'FAILED',
  'EXPIRED',
]);
export type CarPassApiState = z.infer<typeof carPassApiStateSchema>;

/**
 * Car-Pass hard-error issue codes returned by ESB Car-Pass on which the data
 * (or correction) was rejected. The user must correct the request before
 * Car-Pass will accept it.
 *
 * Mirrors `CarPassHardErrorCode` on the backend (DMS BE MR 13443).
 *
 * Note: the response schema validates `issues.hardErrors` as plain `number[]`,
 * not against this enum, so unknown codes are not dropped at parse time and
 * remain visible to the user. This enum is kept for code references and a
 * future i18n key dispatch keyed on the code name.
 */
export enum CarPassHardErrorCode {
  INVALID_MILEAGE = 110,
  UNKNOWN_OR_INACTIVE_COMPANY = 120,
  UNKNOWN_USER_CODE = 121,
  CHASSIS_NUMBER_NOT_UNIQUE = 124,
  PROBLEM_ALREADY_RESOLVED_OR_PERIOD_EXPIRED = 128,
  ONLY_ORIGINAL_USER_CAN_RESOLVE = 130,
  UNKNOWN_CHASSIS_NUMBER = 131,
  ESTABLISHMENT_NOT_PART_OF_COMPANY = 134,
  BOTH_USER_AND_SENDER_CODE_INACTIVE = 146,
  DATE_OLDER_THAN_3_YEARS = 147,
  DUPLICATE_DATA = 150,
  CONFIRMATION_WITHOUT_OPEN_PROBLEMS = 163,
  ONLY_TODAY_DATE_ALLOWED = 180,
  INVALID_CHASSIS_NUMBER_FORMAT = 207,
  NOT_AUTHORISED_FOR_VEHICLE_HISTORY = 242,
}

/**
 * Car-Pass notification issue codes returned by ESB Car-Pass that are
 * informational/warning in nature. The observation may still be recorded; the
 * user can confirm without correcting.
 *
 * Mirrors `CarPassNotificationCode` on the backend (DMS BE MR 13443).
 *
 * Same forward-compat note as `CarPassHardErrorCode`: kept for code references
 * and future i18n dispatch, not used for Zod validation.
 */
export enum CarPassNotificationCode {
  MILEAGE_LOWER_THAN_PREVIOUS = 1,
  DUPLICATE_MILEAGE_FOR_DATE = 123,
  CORRECTION_CAUSED_ANOTHER_ERROR = 129,
  LICENSE_PLATE_NOT_KNOWN_AT_DIV = 190,
  MILEAGE_NOT_REPORTED_IN_TIME = 203,
  MILEAGE_HAS_MORE_DIGITS_THAN_ODOMETER = 205,
  VEHICLE_REGISTERED_AS_WRECK = 206,
  MILEAGE_IS_ROUNDED_NUMBER = 208,
  MILEAGE_HIGHER_THAN_EXPECTED = 209,
  MINOR_ANOMALY = 210,
  SENDER_CODE_INACTIVE = 250,
  USER_CODE_INACTIVE = 251,
}

/**
 * Mileage-related issue codes — the only issues the user can resolve inside our
 * application (correct the reading and re-submit, or confirm it as-is). A
 * Car-Pass observation that returns at least one of these is the sole case the
 * backend routes to `ACTION_REQUIRED`; every other issue goes straight to the
 * terminal `FAILED` state.
 *
 * Mirrors `CarPassMileageIssueCode` on the backend (DMS BE MR 13823 / T20-126718).
 * The codes are a curated subset of `CarPassHardErrorCode` (110) and
 * `CarPassNotificationCode` (1, 203, 205, 208, 209).
 */
export enum CarPassMileageIssueCode {
  MILEAGE_LOWER_THAN_PREVIOUS = 1,
  INVALID_MILEAGE = 110,
  MILEAGE_NOT_REPORTED_IN_TIME = 203,
  MILEAGE_HAS_MORE_DIGITS_THAN_ODOMETER = 205,
  MILEAGE_IS_ROUNDED_NUMBER = 208,
  MILEAGE_HIGHER_THAN_EXPECTED = 209,
}

export const carPassRequestIssuesSchema = z.object({
  hardErrors: z.array(z.number().int()),
  notifications: z.array(z.number().int()),
});
export type CarPassRequestIssues = z.infer<typeof carPassRequestIssuesSchema>;

export const getCarPassRequestResponseBodySchema = z.object({
  id: z.string(),
  vehicleId: z.string(),
  branchId: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  requestGuid: z.string(),
  state: carPassApiStateSchema,
  updatedAt: z.string().nullable(),
  readyAt: z.string().nullable(),
  expiredAt: z.string().nullable(),
  documentId: z.string().nullable(),
  documentPublicUrl: z.string().nullable(),
  issues: carPassRequestIssuesSchema.nullable(),
});
export type GetCarPassRequestResponseBody = z.infer<typeof getCarPassRequestResponseBodySchema>;

export const createCarPassRequestResponseBodySchema = z.object({
  id: z.string(),
});
export type CreateCarPassRequestResponseBody = z.infer<
  typeof createCarPassRequestResponseBodySchema
>;

// Argument types for RTK Query hooks. Per Swagger, none of the mutations take
// a request body — BE re-reads vehicle data by vehicleId on the server side.
export type GetCarPassRequestApiArg = {
  vehicleId: string;
  requestId: string;
};
export type GetCarPassRequestApiResponse = GetCarPassRequestResponseBody;

export type CreateCarPassRequestApiArg = {
  vehicleId: string;
  mileageReadoutDate?: string;
};
export type CreateCarPassRequestApiResponse = CreateCarPassRequestResponseBody;

export type ConfirmCarPassRequestApiArg = {
  vehicleId: string;
  requestId: string;
};
export type ConfirmCarPassRequestApiResponse = void; // 204

export type CorrectCarPassRequestApiArg = {
  vehicleId: string;
  requestId: string;
};
export type CorrectCarPassRequestApiResponse = void; // 204

export type GetInProgressCarPassRequestByVehicleApiArg = {
  vehicleId: string;
};
export type GetInProgressCarPassRequestByVehicleApiResponse = GetCarPassRequestResponseBody;

export type GetReadyCarPassRequestByVehicleApiArg = {
  vehicleId: string;
};
export type GetReadyCarPassRequestByVehicleApiResponse = GetCarPassRequestResponseBody;

export type GetLatestCarPassRequestByVehicleApiArg = {
  vehicleId: string;
};
export type GetLatestCarPassRequestByVehicleApiResponse = GetCarPassRequestResponseBody;
